/**
 * Carrier to'lovi (mijoz jadvalidagi "Payment"): holat belgisi, pul qabul qilish oynasi va shartlar oynasi.
 * Budjet -> Carrier to'lovlari va driver profilidagi joylashtirishlar bir xil komponentlardan foydalanadi.
 * Holat qo'lda qo'yilmaydi: tushgan pul Pul daftariga yoziladi (hisob majburiy, USD -> hisob valyutasiga kun kursi bilan),
 * "to'langan" / "qisman" undan hisoblanadi. Pul faqat tasdiqlangan hire uchun qabul qilinadi (backend qoidasi).
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/lib/api";
import { payError, useAddFeePayment, useUpdatePlacementFee } from "@/features/budget/api";
import type { PlacementFee } from "@/features/budget/types";
import type { PaymentBrief } from "@/features/finance/types";
import type { FeeStatus } from "@/features/drivers/types";
import { dayjs, formatDate, formatMoney, formatMoneyExact } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, Input, NumberInput, Select, TimeInput } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { toast } from "@/components/ui/toast";
import { useMoneyAccounts } from "@/features/finance/api";
import { fmt, nowTime } from "@/features/finance/money";
import { pctError } from "@/features/finance/commission";
import { CommissionField } from "@/features/finance/CommissionField";

/** Ochiq (kutilmoqda yoki qisman) to'lov muddati o'tganmi (bugungi sana bo'yicha) */
export function isOverdue(status: FeeStatus | "", due: string | null): boolean {
  return (status === "pending" || status === "partial") && !!due && dayjs(due).isBefore(dayjs(), "day");
}

/** Pul qabul qilsa bo'ladimi: hire tasdiqlangan va to'lov hali yopilmagan */
export function canReceive(status: FeeStatus | "", approved: boolean): boolean {
  return approved && (status === "pending" || status === "partial");
}

export function FeeBadge({ status, due, paidAt, approved = true, onClick }: { status: FeeStatus | ""; due: string | null; paidAt: string | null; approved?: boolean; onClick?: () => void }) {
  const { t } = useTranslation();
  if (!status) return <span className="text-muted">—</span>;
  const overdue = isOverdue(status, due);
  const waiting = !approved && status === "pending";
  const tone = waiting ? "grey" : overdue || status === "not_paid" ? "red" : status === "paid" ? "green" : status === "partial" ? "orange" : "grey";
  const label = waiting ? t("fee.awaitingApproval") : overdue && status === "partial" ? `${t("fee.partial")} · ${t("fee.overdue")}` : overdue ? t("fee.overdue") : t(`fee.${status}`);
  const date = waiting ? null : status === "paid" || status === "partial" ? paidAt : status === "pending" ? due : null;
  const badge = (
    <Badge tone={tone}>
      {label}
      {date ? ` · ${formatDate(date)}` : ""}
    </Badge>
  );
  if (!onClick) return badge;
  return (
    <button type="button" onClick={onClick} className="rounded-full hover:opacity-80" title={t("actions.edit")}>
      {badge}
    </button>
  );
}

export interface FeeTarget {
  id: number;
  version: number;
  driverName: string;
  carrierName: string;
  start_date: string | null;
  fee_status: FeeStatus | "";
  fee_amount: string | number | null;
  fee_due_date: string | null;
  fee_paid_at: string | null;
  fee_note: string;
  received: PaymentBrief[];
  received_total: string | null;
  remaining: string | null;
  approved: boolean;
}

export function toFeeTarget(p: PlacementFee): FeeTarget {
  return {
    id: p.id, version: p.version, driverName: p.driver.full_name, carrierName: p.carrier.name, start_date: p.start_date,
    fee_status: p.fee_status, fee_amount: p.fee_amount, fee_due_date: p.fee_due_date, fee_paid_at: p.fee_paid_at, fee_note: p.fee_note,
    received: p.received, received_total: String(p.received_total), remaining: p.remaining === null ? null : String(p.remaining), approved: p.approved,
  };
}

const num = (v: string | number | null | undefined) => (v === null || v === undefined || v === "" ? null : Number(v));

function describe(target: FeeTarget, t: (key: string, opts?: Record<string, unknown>) => string): string {
  return [`${target.driverName} → ${target.carrierName}`, target.start_date ? t("placements.started", { date: formatDate(target.start_date) }) : null, target.fee_due_date ? t("placements.dueOn", { date: formatDate(target.fee_due_date) }) : null]
    .filter(Boolean)
    .join(" · ");
}

/** Pul tushdi: summa (USD), hisob (majburiy), sana, izoh. Qolgan summa oldindan to'ldiriladi. */
export function PaymentModal({ target, onOpenChange }: { target: FeeTarget | null; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation("budget");
  const { t: tc } = useTranslation();
  const add = useAddFeePayment();
  const accounts = useMoneyAccounts(!!target);
  const [amount, setAmount] = useState("");
  const [account, setAccount] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState(nowTime());
  const [note, setNote] = useState("");
  const [commission, setCommission] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!target) return;
    setCommission("");
    const remaining = num(target.remaining);
    setAmount(remaining !== null && remaining > 0 ? String(remaining) : "");
    setAccount("");
    setDate(dayjs().format("YYYY-MM-DD"));
    setTime(nowTime());
    setNote("");
    setErrors({});
  }, [target]);

  const selected = accounts.data?.find((a) => String(a.id) === account);

  async function save() {
    if (!target) return;
    const next: Record<string, string> = {};
    if (!account) next.account = t("placements.paymentAccountRequired");
    if (!amount || Number(amount) <= 0) next.amount = t("placements.paymentAmountRequired");
    if (pctError(commission)) next.commission_pct = t("commission.invalid");
    setErrors(next);
    if (Object.keys(next).length) return;
    try {
      await add.mutateAsync({ id: target.id, version: target.version, amount, account: Number(account), date: date || undefined, time: time || undefined, note: note.trim() || undefined, commission_pct: commission });
      toast.success(t("placements.paymentSaved", { name: target.driverName, amount: formatMoney(Number(amount)) }));
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.fields && !err.isConflict) {
        setErrors(Object.fromEntries(Object.entries(err.fields).map(([k, v]) => [k, v.join(" ")])));
        if (!err.fields.amount && !err.fields.account) toast.error(err.message);
        return;
      }
      toast.error(payError(err, tc, tc("toast.failed")));
      if (err instanceof ApiError && err.isConflict) onOpenChange(false);
    }
  }

  const received = num(target?.received_total) ?? 0;
  const total = num(target?.fee_amount);
  const remaining = num(target?.remaining);
  return (
    <Modal
      open={!!target}
      onOpenChange={onOpenChange}
      title={t("placements.paymentTitle")}
      description={target ? describe(target, t) : undefined}
      size="sm"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
          <Button variant="primary" loading={add.isPending} onClick={save}>
            {t("placements.recordPayment")}
          </Button>
        </>
      }
    >
      {target && (
        <div className="mb-3 rounded-xl bg-fill/60 px-3 py-2 text-[12.5px] text-ink-soft">
          {t("placements.remainingBox", { received: formatMoneyExact(received), total: total !== null ? formatMoney(total) : "—", remaining: remaining !== null ? formatMoneyExact(remaining) : "—" })}
        </div>
      )}
      <div className="space-y-3">
        <Field label={t("placements.paymentAmount")} required error={errors.amount} htmlFor="fee-amount">
          <NumberInput id="fee-amount" min={0} step="1" value={amount} onChange={(e) => setAmount(e.target.value)} aria-invalid={errors.amount ? true : undefined} autoFocus />
        </Field>
        {/* Carrier to'lagan summa (USD) to'liq hisoblanadi; komissiya faqat hisobga tushadigan pulni kamaytiradi */}
        <CommissionField direction="in" amount={Number(amount) || 0} currency="USD" value={commission} onChange={setCommission} error={errors.commission_pct} />
        <Field label={t("placements.paymentDate")} required htmlFor="fee-date">
          <div className="flex gap-2">
            <Input id="fee-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            <TimeInput seconds value={time} onChange={(e) => setTime(e.target.value)} aria-label={t("placements.paymentTime")} />
          </div>
        </Field>
        <Field label={t("pay.toAccount")} required error={errors.account} htmlFor="fee-account" hint={selected && selected.currency !== "USD" ? t("placements.convertedHint", { currency: selected.currency }) : undefined}>
          <Select id="fee-account" value={account} onChange={(e) => setAccount(e.target.value)} aria-invalid={errors.account ? true : undefined}>
            <option value="">{t("pay.choose")}</option>
            {(accounts.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} · {fmt(a.balance, a.currency)}
              </option>
            ))}
          </Select>
          {!accounts.isPending && (accounts.data?.length ?? 0) === 0 && <p className="mt-1 text-[12px] text-muted">{t("pay.noAccounts")}</p>}
        </Field>
        <Field label={t("placements.note")} htmlFor="fee-note">
          <Input id="fee-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={120} placeholder={t("placements.paymentNotePlaceholder")} />
        </Field>
      </div>
    </Modal>
  );
}

/** Shartlar: summa, muddat, izoh (pulga tegmaydi) */
export function FeeEditModal({ target, onOpenChange }: { target: FeeTarget | null; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation("budget");
  const { t: tc } = useTranslation();
  const update = useUpdatePlacementFee();
  const [amount, setAmount] = useState("");
  const [due, setDue] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!target) return;
    const a = num(target.fee_amount);
    setAmount(a !== null ? String(a) : "");
    setDue(target.fee_due_date ?? "");
    setNote(target.fee_note ?? "");
  }, [target]);

  async function save() {
    if (!target) return;
    try {
      await update.mutateAsync({ id: target.id, version: target.version, fee_amount: amount === "" ? null : amount, fee_due_date: due || null, fee_note: note });
      toast.success(t("placements.saved", { name: target.driverName }));
      onOpenChange(false);
    } catch (err) {
      toast.error(payError(err, tc, tc("toast.failed")));
      if (err instanceof ApiError && err.isConflict) onOpenChange(false);
    }
  }

  return (
    <Modal
      open={!!target}
      onOpenChange={onOpenChange}
      title={t("placements.editTitle")}
      description={target ? describe(target, t) : undefined}
      size="sm"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{tc("actions.cancel")}</Button>
          <Button variant="primary" loading={update.isPending} onClick={save}>
            {tc("actions.save")}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("placements.amount")} hint={t("placements.amountHint")} htmlFor="fee-edit-amount">
            <NumberInput id="fee-edit-amount" min={0} step="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0" />
          </Field>
          <Field label={t("placements.dueDate")} htmlFor="fee-edit-due">
            <Input id="fee-edit-due" type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          </Field>
        </div>
        <Field label={t("placements.note")} htmlFor="fee-edit-note">
          <Input id="fee-edit-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} placeholder={t("placements.notePlaceholder")} />
        </Field>
        {target && target.received.length > 0 && (
          <p className="text-[12px] text-muted">{t("placements.editReceivedNote", { received: formatMoney(num(target.received_total) ?? 0) })}</p>
        )}
      </div>
    </Modal>
  );
}
